import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { API, apiError } from "@/lib/api";
import { Plus, Download, Search, Trash2, Phone, PhoneForwarded, Plane, Briefcase, ThumbsUp, Clock, UserPlus, PhoneOff, Building2 } from "lucide-react";
import { LeadRow } from "@/components/telecaller/LeadRow";
import { AddLeadsDialog } from "@/components/telecaller/AddLeadsDialog";
import { NextCallCard } from "@/components/telecaller/NextCallCard";
import { InfoPanel } from "@/components/telecaller/InfoPanel";
import { DistrictBar, SetDistrict } from "@/components/telecaller/DistrictBar";

const today = () => new Date().toISOString().slice(0, 10);
const VIEWS = [
  { k: "all", label: "All" }, { k: "today", label: "Today's follow-ups" }, { k: "overdue", label: "Overdue" },
  { k: "new", label: "New" }, { k: "interested", label: "Interested" }, { k: "follow_up", label: "Follow-up" }, { k: "not_received", label: "Not received" }, { k: "approved", label: "Approved" }, { k: "make_agent", label: "Made partner" },
];
const match = (l, v) => v === "all" ? true : v === "today" ? l.next_follow_up === today()
  : v === "overdue" ? (l.next_follow_up && l.next_follow_up < today()) : l.status === v;

const HEAD = ["", "Name", "Phone", "WhatsApp", "City", "Status", "Demand / Notes", "Next follow-up", "Travel date", "Group", "Calls", "Last called", "Actions"];

export function TelecallerSheet({ client, token }) {
  const [data, setData] = useState({ leads: [], groups: [] });
  const [view, setView] = useState("all");
  const [q, setQ] = useState("");
  const [dist, setDist] = useState("");
  const [sel, setSel] = useState([]);
  const [add, setAdd] = useState(false);
  const [cat, setCat] = useState("umrah");
  const [current, setCurrent] = useState(null);

  const load = useCallback(async () => {
    try { setData((await client.get("/telecaller/leads")).data); } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  }, [client]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSel([]); }, [cat, view, q, dist]);

  const inCat = useMemo(() => data.leads.filter((l) => (l.category || "umrah") === cat), [data.leads, cat]);
  const counts = useMemo(() => Object.fromEntries(VIEWS.map((v) => [v.k, inCat.filter((l) => match(l, v.k)).length])), [inCat]);
  const inDist = (l) => !dist || (l.district || "Unknown") === dist;
  const catCount = (c) => data.leads.filter((l) => (l.category || "umrah") === c).length;
  const rows = inCat.filter((l) => inDist(l) && match(l, view) && (!q || `${l.name} ${l.phone} ${l.city} ${l.district} ${l.demand}`.toLowerCase().includes(q.toLowerCase())));
  const setDistrictMany = async (district) => {
    try { const r = (await client.post("/telecaller/leads/district-many", { ids: sel, district })).data; toast.success(`${r.updated} moved to ${district}`); setSel([]); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const nextCall = async () => {
    try {
      const lead = (await client.post(`/telecaller/next?category=${cat}${current ? `&skip=${current.id}` : ""}`)).data;
      setCurrent(lead);
      load();
    } catch (err) { setCurrent(null); toast.info(apiError(err.response?.data?.detail)); }
  };

  const save = async (id, patch) => {
    try {
      const r = (await client.put(`/telecaller/leads/${id}`, patch)).data;
      if (r.deleted) { toast.success("Not interested — number deleted"); setData((d) => ({ ...d, leads: d.leads.filter((l) => l.id !== id) })); return; }
      if (r.contact_id && !data.leads.find((l) => l.id === id)?.contact_id) toast.success("Approved — added to Customers in the chosen group");
      setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? r : l)) }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const called = async (id) => {
    const r = (await client.post(`/telecaller/leads/${id}/called`)).data;
    setData((d) => ({ ...d, leads: d.leads.map((l) => (l.id === id ? r : l)) }));
    setCurrent((c) => (c && c.id === id ? r : c));
  };
  const agreed = async (l, reason) => {
    const label = reason === "partner" ? "Agent leads (agreed to work with us)" : "Umrah customers (agreed to buy a package)";
    if (!window.confirm(`Move ${l.name || l.phone} to ${label}?`)) return;
    try {
      const r = (await client.post(`/telecaller/leads/${l.id}/agreed`, { reason })).data;
      setData((d) => ({ ...d, leads: d.leads.map((x) => (x.id === l.id ? r : x)) }));
      toast.success(`Moved to ${label}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (l) => {
    if (!window.confirm(`Delete ${l.name || l.phone}?`)) return;
    await client.delete(`/telecaller/leads/${l.id}`);
    toast.success("Deleted"); load();
  };
  const delMany = async () => {
    if (!window.confirm(`Delete ${sel.length} selected numbers?`)) return;
    await client.post("/telecaller/leads/delete-many", { ids: sel });
    setSel([]); toast.success("Deleted"); load();
  };
  const statusMany = async (status, label) => {
    try {
      const r = (await client.post("/telecaller/leads/status-many", { ids: sel, status })).data;
      setSel([]); toast.success(`${r.updated} numbers marked ${label}`); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const exportXlsx = async () => {
    const r = await fetch(`${API}/telecaller/export.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const url = URL.createObjectURL(await r.blob());
    Object.assign(document.createElement("a"), { href: url, download: "telecaller-sheet.xlsx" }).click();
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = rows.length > 0 && rows.every((l) => sel.includes(l.id));
  const toggleAll = () => setSel(allSel ? [] : rows.map((l) => l.id));

  return (
    <div className="space-y-4" data-testid="telecaller-sheet">
      <div className="flex flex-wrap gap-2">
        {[["umrah", "Umrah customers", Plane], ["job", "Job customers", Briefcase], ["agent", "Agent leads", UserPlus], ["travels", "Other Travels (separate)", Building2]].map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => { setCat(k); setSel([]); setCurrent(null); }} data-testid={`tc-cat-${k}`}
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-bold transition-colors ${cat === k ? "bg-gold text-[#0a2e22] border-gold" : "border-gold/40 text-gold hover:bg-gold/10"}`}>
            <Icon className="w-4 h-4" /> {label} <span className="opacity-70">({catCount(k)})</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["Total numbers", inCat.length, "text-white"], ["Due today", counts.today, "text-gold"], ["Overdue", counts.overdue, "text-red-300"], ["Not received", counts.not_received, "text-orange-300"], [cat === "agent" ? "Made partner" : "Approved", cat === "agent" ? counts.make_agent : counts.approved, "text-cyan-300"]].map(([k, v, c]) => (
          <Card key={k} className="p-3 bg-card/40 border-gold/20" data-testid={`tc-stat-${k.toLowerCase().replace(/ /g, "-")}`}>
            <p className="eyebrow text-muted-foreground">{k}</p><p className={`text-2xl font-serif mt-1 ${c}`}>{v}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={nextCall} className="bg-emerald-500 text-[#04221a] hover:bg-emerald-400 h-9 gap-1.5 font-bold" data-testid="tc-next-btn"><PhoneForwarded className="w-4 h-4" /> Call next number</Button>
        <Button onClick={() => setAdd(true)} className="bg-gold text-[#0a2e22] hover:bg-gold/90 h-9 gap-1.5 font-semibold" data-testid="tc-add-btn"><Plus className="w-4 h-4" /> Add numbers</Button>
        <Button variant="outline" onClick={exportXlsx} className="border-gold/40 text-gold h-9 gap-1.5" data-testid="tc-export-btn"><Download className="w-4 h-4" /> Export Excel</Button>
        {sel.length > 0 && <Button variant="outline" onClick={() => statusMany("interested", "Interested")} className="border-emerald-400/40 text-emerald-300 h-9 gap-1.5" data-testid="tc-mark-interested-btn"><ThumbsUp className="w-4 h-4" /> Mark {sel.length} Interested</Button>}
        {sel.length > 0 && <Button variant="outline" onClick={() => statusMany("not_received", "Not received")} className="border-orange-300/40 text-orange-300 h-9 gap-1.5" data-testid="tc-mark-notreceived-btn"><PhoneOff className="w-4 h-4" /> Mark {sel.length} Not received</Button>}
        {sel.length > 0 && <Button variant="outline" onClick={() => statusMany("follow_up", "Follow-up")} className="border-gold/40 text-gold h-9 gap-1.5" data-testid="tc-mark-followup-btn"><Clock className="w-4 h-4" /> Mark {sel.length} Follow-up</Button>}
        {sel.length > 0 && <SetDistrict count={sel.length} onSet={setDistrictMany} />}
        {sel.length > 0 && <Button variant="outline" onClick={delMany} className="border-red-400/40 text-red-300 h-9 gap-1.5" data-testid="tc-delete-many-btn"><Trash2 className="w-4 h-4" /> Delete {sel.length}</Button>}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, number, city, notes" className="pl-8 h-9 bg-card/60" data-testid="tc-search" />
        </div>
      </div>

      {cat === "travels" && (
        <Card className="p-3 bg-amber-500/10 border-amber-400/40 text-sm text-amber-100" data-testid="tc-travels-note">
          This list is kept separate from your own customers. Numbers from other travels stay here until they agree — then press <b>Agreed: work with us</b> (moves to Agent leads) or <b>Agreed: buy package</b> (moves to Umrah customers).
        </Card>
      )}

      <NextCallCard lead={current} onCalled={called} onNext={nextCall} onClose={() => setCurrent(null)} />

      <DistrictBar leads={inCat} value={dist} onChange={setDist} />

      <div className="flex flex-wrap gap-1.5">
        {VIEWS.map((v) => (
          <button key={v.k} type="button" onClick={() => setView(v.k)} data-testid={`tc-view-${v.k}`}
            className={`px-3 h-8 rounded-full text-xs border transition-colors ${view === v.k ? "bg-gold text-[#0a2e22] border-gold font-bold" : "border-border/60 text-white hover:border-gold/50"}`}>
            {v.label} <span className="opacity-70">({counts[v.k]})</span>
          </button>
        ))}
      </div>

      <InfoPanel client={client} cat={cat} />

      <div className="flex flex-wrap items-center gap-4 text-sm text-white" data-testid="tc-colour-legend">
        {[["bg-transparent border border-white/40", "Not called yet"], ["bg-yellow-400/60", "Called"], ["bg-emerald-500/70", "Interested"], ["bg-sky-500/70", "Follow-up"], ["bg-yellow-200", "Light yellow = called today"], ["bg-emerald-300", "Light green = Interested, called today"], ["bg-sky-300", "Light blue = Follow-up, called today"]].map(([c, t]) => (
          <span key={t} className="inline-flex items-center gap-2"><span className={`w-4 h-4 rounded ${c}`} />{t}</span>
        ))}
      </div>

      <Card className="bg-[#031C15]/80 border-gold/30 overflow-x-auto shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <table className="w-full text-left border-collapse min-w-[1400px]">
          <thead className="sticky top-0 bg-[#05261D]">
            <tr>{HEAD.map((h, i) => <th key={h || "sel"} className="text-xs font-bold uppercase tracking-wider text-gold px-2 py-3 border-b border-gold/40 whitespace-nowrap">{i === 0 ? <input type="checkbox" checked={allSel} onChange={toggleAll} title="Select all" data-testid="tc-select-all" /> : h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <LeadRow key={`${l.id}-${l.updated_at}-${l.call_count}-${l.assigned_to}`} l={l} groups={data.groups} selected={sel.includes(l.id)} onSelect={toggle} onSave={save} onCalled={called} onDelete={del} onAgreed={agreed} highlight={current?.id === l.id} />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="py-12 text-center text-sm text-muted-foreground" data-testid="tc-empty">
            <Phone className="w-6 h-6 mx-auto text-gold mb-2" /> No numbers here yet — click "Add numbers" to add single, bulk or import Excel / PDF.
          </div>
        )}
      </Card>
      <p className="text-[11px] text-muted-foreground">Tip: "Call next number" gives you the next person to call and reserves them under your name so no one else calls the same person. Call and WhatsApp open on your own phone, and every call is saved with your name. Rows are coloured: no colour = not called yet, yellow = called, green = interested, blue = follow-up. "Not interested" deletes the number; for Umrah, "Approved" + a group adds them to Customers and sends the Umrah Guide Book.</p>
      <AddLeadsDialog client={client} open={add} onOpenChange={setAdd} onDone={load} category={cat} />
    </div>
  );
}
